import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import useDeviceDetect from "../hooks/useDeviceDetect";
import moment from "moment-timezone";
import "./Dashboard.css";

const SERVER_URL = `${process.env.REACT_APP_BASE_API_URL}/dashboard`;

// Function to format date and time
const formatDateTime = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return moment(date).format("DD/MM/YYYY HH:mm");
};

// Helper function to normalize strings
const normalizeString = (str) => {
  return typeof str === "string" ? str.trim().toLowerCase() : "";
};

const Dashboard = () => {
  const [records, setRecords] = useState([]);
  const [error, setError] = useState("");
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [cardCounts, setCardCounts] = useState({
    liveJourneys: 0,
    criticalCheck: 0,
    dueForChecking: 0,
    stoppedTrucks: 0,
    ibStoppageTrucks: 0,
  });
  const [timer, setTimer] = useState(180);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const [alertedRecords, setAlertedRecords] = useState(new Set());
  const [audio] = useState(() => {
    const audioObj = new Audio("/sounds/alarm.mp3");
    audioObj.muted = false;
    return audioObj;
  });
  const [reportAudio] = useState(() => {
    const audioObj = new Audio("/sounds/ElevenLabs_Text_to_Speech_audio.mp3");
    audioObj.muted = false;
    return audioObj;
  });
  const [isMuted, setIsMuted] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationTimer, setNotificationTimer] = useState(15); 
  const navigate = useNavigate();
  const { isMobile } = useDeviceDetect();

  const notificationStyle = {
    position: "fixed",
    top: "20px",
    right: "20px",
    backgroundColor: "white",
    padding: "20px",
    borderRadius: "10px",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
    zIndex: 1000,
    width: "300px",
    border: "1px solid #e2e8f0",
    animation: "slideIn 0.5s ease-out",
  };

  const notificationHeaderStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "10px",
    paddingBottom: "10px",
    borderBottom: "1px solid #e2e8f0",
  };

  const notificationContentStyle = {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
  };

  const closeButtonStyle = {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "18px",
    color: "#64748b",
    padding: "5px",
  };

  const calculateMinutesSinceLastCheck = useCallback((ivmsCheckDate) => {
    if (!ivmsCheckDate) return Infinity;
    const currentTime = moment.tz("Asia/Muscat");
    const checkTime = moment(ivmsCheckDate);
    return currentTime.diff(checkTime, "minutes");
  }, []);

  const calculateCardCounts = useCallback(
    (data) => {
      let counts = {
        liveJourneys: 0,
        criticalCheck: 0,
        dueForChecking: 0,
        stoppedTrucks: 0,
        ibStoppageTrucks: 0,
      };

      data.forEach((item) => {
        const minutesSinceLastCheck = calculateMinutesSinceLastCheck(
          item.IVMS_CHECK_DATE
        );
        const normalizedJPStatus = normalizeString(item.JP_STATUS);
        const normalizedRemarks = normalizeString(item.REMARKS);
        const normalizedRemarks1 = normalizeString(item.REMARKS1);

        if (!["closed"].includes(normalizedJPStatus)) counts.liveJourneys++;
        if (
          minutesSinceLastCheck > 90 &&
          !["stopped"].includes(normalizedRemarks) &&
          !["closed"].includes(normalizedJPStatus)
        ) {
          counts.criticalCheck++;
        }
        // Keep original 60 minute threshold for card count
        if (
          ((minutesSinceLastCheck > 60 && minutesSinceLastCheck <= 90) ||
            isNaN(minutesSinceLastCheck)) &&
          !["stopped"].includes(normalizedRemarks) &&
          !["closed"].includes(normalizedJPStatus)
        ) {
          counts.dueForChecking++;
        }
        if (
          ["stopped"].includes(normalizedRemarks) &&
          !["closed"].includes(normalizedJPStatus)
        ) {
          counts.stoppedTrucks++;
        }
        // Add this block to count the "I/B Stoppage"
        if (
          ["breakdown", "incident"].includes(normalizedRemarks1) &&
          !["closed"].includes(normalizedJPStatus)
        ) {
          counts.ibStoppageTrucks++;
        }
      });
      setCardCounts(counts);
    },
    [calculateMinutesSinceLastCheck]
  );

  // Modify playReportAlert function
  const playReportAlert = useCallback(() => {
    if (!isMuted) {
      reportAudio.currentTime = 0;
      reportAudio
        .play()
        .catch((error) => console.log("Report audio play failed:", error));

      setShowNotification(true);
      setNotificationTimer(15); // Changed from 60 to 15
    }
  }, [reportAudio, isMuted]);

  // Modify notification timer effect
  useEffect(() => {
    let timerId;
    if (showNotification) {
      timerId = setInterval(() => {
        setNotificationTimer((prev) => {
          if (prev <= 1) {
            setShowNotification(false);
            return 15; // Changed from 60 to 15
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerId) clearInterval(timerId);
    };
  }, [showNotification]);

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => {
      const newMuted = !prev;
      audio.muted = newMuted;
      reportAudio.muted = newMuted;
      return newMuted;
    });
  }, [audio, reportAudio]);

  const checkForDueRecords = useCallback(
    (data) => {
      // Early warning records for alarm (56-60 minutes)
      const earlyWarningRecords = data.filter((item) => {
        const minutesSinceLastCheck = calculateMinutesSinceLastCheck(
          item.IVMS_CHECK_DATE
        );
        const normalizedJPStatus = normalizeString(item.JP_STATUS);
        const normalizedRemarks = normalizeString(item.REMARKS);

        return (
          minutesSinceLastCheck > 55 &&
          minutesSinceLastCheck <= 60 &&
          !["stopped"].includes(normalizedRemarks) &&
          !["closed"].includes(normalizedJPStatus)
        );
      });

      // Play alarm for early warning records
      const newDueRecords = earlyWarningRecords.filter(
        (record) => !alertedRecords.has(record.JOURNEY_PLANE_NO)
      );

      if (newDueRecords.length > 0 && !isMuted) {
        audio.currentTime = 0;
        if (!audio.muted) {
          audio
            .play()
            .catch((error) => console.log("Audio play failed:", error));
        }

        const updatedAlertedRecords = new Set(alertedRecords);
        newDueRecords.forEach((record) =>
          updatedAlertedRecords.add(record.JOURNEY_PLANE_NO)
        );
        setAlertedRecords(updatedAlertedRecords);
      }
    },
    [alertedRecords, audio, calculateMinutesSinceLastCheck, isMuted]
  );

  const checkReportTime = useCallback(() => {
    const now = moment.tz("Asia/Muscat");
    const hours = now.hours();
    const minutes = now.minutes();

    console.log("Current time (Muscat):", hours + ":" + minutes);

    if (hours === 8 && minutes === 30) {
      console.log("Playing report alert!");
      playReportAlert();
    }
  }, [playReportAlert]);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(SERVER_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setRecords(response.data);
      const filtered = response.data.filter(
        (item) => !["closed"].includes(normalizeString(item.JP_STATUS))
      );
      setFilteredRecords(filtered);
      setError("");
      calculateCardCounts(filtered);
      checkForDueRecords(filtered);
      setTimer(180);
    } catch (error) {
      handleFetchError(error);
    }
  }, [calculateCardCounts, checkForDueRecords]);

  const handleFetchError = (error) => {
    console.error("Fetch error:", error);
    setError("Error fetching dashboard data");
  };

  useEffect(() => {
    fetchData();

    const intervalId = setInterval(() => {
      fetchData();
    }, 180000); // Fetch every 3 minutes

    return () => {
      clearInterval(intervalId);
    };
  }, [fetchData]);

  useEffect(() => {
    const timerId = setInterval(() => {
      setTimer((prevTimer) => {
        if (prevTimer <= 0) {
          fetchData();
          return 180;
        }
        return prevTimer - 1;
      });
    }, 1000);

    return () => clearInterval(timerId);
  }, [fetchData]);

  useEffect(() => {
    // Check immediately on component mount
    checkReportTime();

    // Check every minute
    const reportInterval = setInterval(checkReportTime, 60000);

    // Request notification permission
    if (Notification.permission === "default") {
      Notification.requestPermission();
    }

    return () => clearInterval(reportInterval);
  }, [checkReportTime]);

  const handleCardClick = (type) => {
    const filtered = records.filter((item) => {
      const minutesSinceLastCheck = calculateMinutesSinceLastCheck(
        item.IVMS_CHECK_DATE
      );
      const normalizedJPStatus = normalizeString(item.JP_STATUS);
      const normalizedRemarks = normalizeString(item.REMARKS);
      const normalizedRemarks1 = normalizeString(item.REMARKS1);

      const isValidHours =
        (minutesSinceLastCheck > 60 && minutesSinceLastCheck < 90) ||
        isNaN(minutesSinceLastCheck);

      const isValidStatus =
        !["closed"].includes(normalizedJPStatus) &&
        !["stopped"].includes(normalizedRemarks);

      switch (type) {
        case "live":
          return !["closed"].includes(normalizedJPStatus);
        case "critical":
          return (
            minutesSinceLastCheck > 90 &&
            !["stopped"].includes(normalizedRemarks) &&
            !["closed"].includes(normalizedJPStatus)
          );
        case "due":
          return isValidHours && isValidStatus;
        case "stopped":
          return (
            ["stopped"].includes(normalizedRemarks) &&
            !["closed"].includes(normalizedJPStatus)
          );
        case "ibStoppage":
          return (
            ["breakdown", "incident"].includes(normalizedRemarks1) &&
            !["closed"].includes(normalizedJPStatus)
          );
        default:
          return true;
      }
    });
    setFilteredRecords(filtered);
  };

  const handleSearch = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchTerm(value);
    setFilteredRecords(
      records.filter(
        (record) =>
          Object.values(record).some(
            (val) => val !== null && normalizeString(val).includes(value)
          ) && !["closed"].includes(normalizeString(record.JP_STATUS))
      )
    );
  };

  const journeyPlaneDateCounts = useMemo(() => {
    return filteredRecords.reduce((acc, record) => {
      const date = formatDateTime(record.JOURNEY_PLANE_DATE).split(" ")[0];
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {});
  }, [filteredRecords]);

  const trackersCount = useMemo(() => {
    let totalCounts = {
      liveJPS: 0,
      criticalCheck: 0,
      dueForChecking: 0,
    };

    const counts = records.reduce((acc, record) => {
      const tracker = record.TRACKER?.trim().toLowerCase();
      if (!tracker || ["closed"].includes(normalizeString(record.JP_STATUS)))
        return acc;

      acc[tracker] = acc[tracker] || {
        liveJPS: 0,
        criticalCheck: 0,
        dueForChecking: 0,
      };

      const minutesSinceLastCheck = calculateMinutesSinceLastCheck(
        record.IVMS_CHECK_DATE
      );
      const normalizedJPStatus = normalizeString(record.JP_STATUS);
      const normalizedRemarks = normalizeString(record.REMARKS);

      if (!["closed"].includes(normalizedJPStatus)) {
        acc[tracker].liveJPS++;
        totalCounts.liveJPS++;
      }

      if (
        minutesSinceLastCheck > 90 &&
        !["stopped"].includes(normalizedRemarks) &&
        !["closed"].includes(normalizedJPStatus)
      ) {
        acc[tracker].criticalCheck++;
        totalCounts.criticalCheck++;
      }

      if (
        ((minutesSinceLastCheck > 60 && minutesSinceLastCheck <= 90) ||
          isNaN(minutesSinceLastCheck)) &&
        !["stopped"].includes(normalizedRemarks) &&
        !["closed"].includes(normalizedJPStatus)
      ) {
        acc[tracker].dueForChecking++;
        totalCounts.dueForChecking++;
      }

      return acc;
    }, {});

    // Log totals to verify
    console.log("Card Counts:", cardCounts);
    console.log("Tracker Totals:", totalCounts);

    return counts;
  }, [records, calculateMinutesSinceLastCheck]);

  const sortedRecords = useMemo(() => {
    let sortableRecords = [...filteredRecords];
    if (sortConfig.key !== null) {
      sortableRecords.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableRecords;
  }, [filteredRecords, sortConfig]);

  const getClassNamesFor = (name) => {
    if (!sortConfig) {
      return;
    }
    return sortConfig.key === name ? sortConfig.direction : undefined;
  };

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const renderNotification = () => {
    if (!showNotification) return null;

    return (
      <div style={notificationStyle}>
        <div style={notificationHeaderStyle}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <i className="fas fa-bell" style={{ color: "#007bff" }}></i>
            <span style={{ fontWeight: "600", color: "#1e293b" }}>
              Report Reminder
            </span>
          </div>
          <button
            style={closeButtonStyle}
            onClick={() => setShowNotification(false)}
            title="Close"
          >
            ×
          </button>
        </div>
        <div style={notificationContentStyle}>
          <div>
            <p style={{ margin: "0 0 8px 0", color: "#475569" }}>
              It's time to prepare the daily report!
            </p>
            <p style={{ margin: "0", fontSize: "12px", color: "#64748b" }}>
              This notification will close in {notificationTimer} seconds
            </p>
          </div>
        </div>
      </div>
    );
  };

  const smartInsight = (() => {
    const live = cardCounts.liveJourneys || 0;
    const critical = cardCounts.criticalCheck || 0;
    const due = cardCounts.dueForChecking || 0;
    const stopped = cardCounts.stoppedTrucks || 0;
    if (critical > 0) {
      const exposure = live ? Math.round((critical / live) * 100) : 0;
      return { level: "critical", icon: "fa-bolt", title: "Immediate check priority",
        message: `${critical} journeys need urgent IVMS follow-up (${exposure}% of live journeys). Start with the oldest red-highlighted records.` };
    }
    if (due > 0) return { level: "warning", icon: "fa-clock", title: "Checks approaching threshold",
      message: `${due} journeys are due for checking. Completing them now will prevent critical escalation.` };
    if (stopped > 0) return { level: "notice", icon: "fa-pause-circle", title: "Stopped trucks need review",
      message: `${stopped} stopped trucks are recorded. Verify their remarks and operational status.` };
    return { level: "healthy", icon: "fa-check-circle", title: "Operations currently stable",
      message: "No urgent or due IVMS checks are detected in the current live journey view." };
  })();

  return (
    <div className={`dashboard-container ${isMobile ? "mobile" : "desktop"}`}>
      <div className="btn-main">
        <div>
          <button
            className="navigate-button"
            onClick={() => navigate("/main")}
            title="Back to Main Page"
          >
            <i className="fas fa-arrow-left"></i>
          </button>
        </div>

        <div className="search-container top-search-container">
          <i className="fas fa-search"></i>
          <input
            type="text"
            className="search-input"
            placeholder="Search journey, truck, driver, tracker or location..."
            value={searchTerm}
            onChange={handleSearch}
          />
          {searchTerm && (
            <button className="clear-search-button" onClick={() => setSearchTerm("")} title="Clear search" aria-label="Clear search">&times;</button>
          )}
        </div>

        <div className="btn-main">
          <button
            className="mute-button"
            onClick={toggleMute}
            title={isMuted ? "Unmute Alerts" : "Mute Alerts"}
          >
            <i
              className={`fas ${isMuted ? "fa-volume-mute" : "fa-volume-up"}`}
            ></i>
          </button>
          <div className="timer">
            <i className="fas fa-clock"></i>
            <span>
              {Math.floor(timer / 60)}:{("0" + (timer % 60)).slice(-2)}
            </span>
          </div>
        </div>
      </div>
      {/* <h1 className="dashboard-title">JMCC Dashboard</h1> */}
      {error ? (
        <p className="text-center text-danger">{error}</p>
      ) : (
        <div>
          <div className="card-container">
            <div
              onClick={() => handleCardClick("live")}
              className="card card live"
            >
              <i className="fas fa-route"></i>
              <div>Live Journeys: {cardCounts.liveJourneys}</div>
            </div>
            <div
              onClick={() => handleCardClick("critical")}
              className="card card critical"
            >
              <i className="fas fa-exclamation-triangle"></i>
              <div>Critical Check: {cardCounts.criticalCheck}</div>
            </div>
            <div
              onClick={() => handleCardClick("due")}
              className="card card due"
            >
              <i className="fas fa-clock"></i>
              <div>Due For Checking: {cardCounts.dueForChecking}</div>
            </div>
            <div
              onClick={() => handleCardClick("stopped")}
              className="card card stopped"
            >
              <i className="fas fa-stop-circle"></i>
              <div>Stopped Trucks: {cardCounts.stoppedTrucks}</div>
            </div>
            <div
              onClick={() => handleCardClick("ibStoppage")}
              className="card card ib-stoppage"
            >
              <i className="fas fa-truck-loading"></i>
              <div>I/B Stoppage: {cardCounts.ibStoppageTrucks}</div>
            </div>
          </div>
          <div className={`smart-insight ${smartInsight.level}`}>
            <div className="smart-insight-icon"><i className={`fas ${smartInsight.icon}`}></i></div>
            <div><span>SMART OPERATIONS INSIGHT</span><strong>{smartInsight.title}</strong><p>{smartInsight.message}</p></div>
            <small>Based on current dashboard data</small>
          </div>
          <div className="tables-container">
            <div className="main-table table-scroll">
              <table className="main-table">
                <thead>
                  <tr>
                    <th
                      onClick={() => requestSort("JOURNEY_PLANE_NO")}
                      className={getClassNamesFor("JOURNEY_PLANE_NO")}
                    >
                      Journey Plan No
                      {sortConfig.key === "JOURNEY_PLANE_NO" && (
                        <span
                          className={`sort-arrow ${sortConfig.direction}`}
                        />
                      )}
                    </th>
                    <th
                      onClick={() => requestSort("SJM")}
                      className={getClassNamesFor("SJM")}
                    >
                      Journey Manager
                      {sortConfig.key === "SJM" && (
                        <span
                          className={`sort-arrow ${sortConfig.direction}`}
                        />
                      )}
                    </th>
                    <th
                      onClick={() => requestSort("JOURNEY_PLANE_DATE")}
                      className={getClassNamesFor("JOURNEY_PLANE_DATE")}
                    >
                      Issued Time
                      {sortConfig.key === "JOURNEY_PLANE_DATE" && (
                        <span
                          className={`sort-arrow ${sortConfig.direction}`}
                        />
                      )}
                    </th>
                    <th
                      onClick={() => requestSort("TRACKER")}
                      className={getClassNamesFor("TRACKER")}
                    >
                      Tracker
                      {sortConfig.key === "TRACKER" && (
                        <span
                          className={`sort-arrow ${sortConfig.direction}`}
                        />
                      )}
                    </th>
                    <th
                      onClick={() => requestSort("SCHEDULED_VEHICLE")}
                      className={getClassNamesFor("SCHEDULED_VEHICLE")}
                    >
                      Truck
                      {sortConfig.key === "SCHEDULED_VEHICLE" && (
                        <span
                          className={`sort-arrow ${sortConfig.direction}`}
                        />
                      )}
                    </th>
                    <th
                      onClick={() => requestSort("DRIVER_NAME")}
                      className={getClassNamesFor("DRIVER_NAME")}
                    >
                      Driver Name
                      {sortConfig.key === "DRIVER_NAME" && (
                        <span
                          className={`sort-arrow ${sortConfig.direction}`}
                        />
                      )}
                    </th>
                    <th
                      onClick={() => requestSort("IVMS_POINT")}
                      className={getClassNamesFor("IVMS_POINT")}
                    >
                      Last IVMS POINT
                      {sortConfig.key === "IVMS_POINT" && (
                        <span
                          className={`sort-arrow ${sortConfig.direction}`}
                        />
                      )}
                    </th>
                    <th
                      onClick={() => requestSort("IVMS_CHECK_DATE")}
                      className={getClassNamesFor("IVMS_CHECK_DATE")}
                    >
                      Last Check Time
                      {sortConfig.key === "IVMS_CHECK_DATE" && (
                        <span
                          className={`sort-arrow ${sortConfig.direction}`}
                        />
                      )}
                    </th>
                    <th
                      onClick={() => requestSort("IVMS_CHECK_DATE")}
                      className={getClassNamesFor("IVMS_CHECK_DATE")}
                    >
                      Minutes Since Last Check
                      {sortConfig.key === "IVMS_CHECK_DATE" && (
                        <span
                          className={`sort-arrow ${sortConfig.direction}`}
                        />
                      )}
                    </th>
                    <th
                      onClick={() => requestSort("NEXT_POINT")}
                      className={getClassNamesFor("NEXT_POINT")}
                    >
                      Next Point
                      {sortConfig.key === "NEXT_POINT" && (
                        <span
                          className={`sort-arrow ${sortConfig.direction}`}
                        />
                      )}
                    </th>
                    {/* <th
                      onClick={() => requestSort("NEXT_ARRIVAL_DATE")}
                      className={getClassNamesFor("NEXT_ARRIVAL_DATE")}
                    >
                      Next Arrival Time
                      {sortConfig.key === "NEXT_ARRIVAL_DATE" && (
                        <span
                          className={`sort-arrow ${sortConfig.direction}`}
                        />
                      )}
                    </th> */}
                    <th
                      onClick={() => requestSort("OFFLOAD_POINT")}
                      className={getClassNamesFor("OFFLOAD_POINT")}
                    >
                      OFFLOAD_POINT
                      {sortConfig.key === "OFFLOAD_POINT" && (
                        <span
                          className={`sort-arrow ${sortConfig.direction}`}
                        />
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRecords.map((record) => {
                    const minutesSinceLastCheck =
                      calculateMinutesSinceLastCheck(record.IVMS_CHECK_DATE);
                    const normalizedRemarks = normalizeString(record.REMARKS);
                    const normalizedJPStatus = normalizeString(
                      record.JP_STATUS
                    );

                    // Early warning styling for entire row
                    let rowStyle = {};
                    let minutesStyle = {};

                    // Due for checking - continuous blinking for 60-90 minutes
                    if (
                      minutesSinceLastCheck > 60 &&
                      minutesSinceLastCheck <= 90 &&
                      !["stopped"].includes(normalizedRemarks) &&
                      !["closed"].includes(normalizedJPStatus)
                    ) {
                      rowStyle = {
                        background:
                          "linear-gradient(45deg, #ea580c30, #c2410c30)", // Matching "due" card color with transparency
                        animation:
                          "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
                        transition: "all 0.3s ease",
                        color: "#ea580c", // Matching text color to card
                      };
                    }
                    // Enhanced early warning styling with modern effect
                    else if (
                      minutesSinceLastCheck > 55 &&
                      minutesSinceLastCheck <= 60 &&
                      !["stopped"].includes(normalizedRemarks) &&
                      !["closed"].includes(normalizedJPStatus)
                    ) {
                      rowStyle = {
                        background: "linear-gradient(45deg, #ff7b00, #ffa563)",
                        animation: "glow 1.5s ease-in-out infinite alternate",
                        boxShadow: "0 0 10px rgba(255, 123, 0, 0.5)",
                        transition: "all 0.3s ease",
                      };
                    }

                    // Regular status styling for minutes cell
                    if (
                      minutesSinceLastCheck > 90 &&
                      !["stopped"].includes(normalizedRemarks) &&
                      !["closed"].includes(normalizedJPStatus)
                    ) {
                      minutesStyle = { backgroundColor: "#efb5b5" };
                    } else if (["stopped"].includes(normalizedRemarks)) {
                      minutesStyle = { backgroundColor: "#DEEFFF" };
                    } else if (
                      minutesSinceLastCheck > 60 &&
                      minutesSinceLastCheck <= 90
                    ) {
                      minutesStyle = { backgroundColor: "#e6e6e6" };
                    }

                    return (
                      <tr key={record.ID} style={rowStyle}>
                        <td>{record.JOURNEY_PLANE_NO}</td>
                        <td>{record.SJM}</td>
                        <td>{formatDateTime(record.JOURNEY_PLANE_DATE)}</td>
                        <td>{record.TRACKER}</td>
                        <td>{record.SCHEDULED_VEHICLE}</td>
                        <td>{record.DRIVER_NAME}</td>
                        <td>{record.IVMS_POINT}</td>
                        <td>{formatDateTime(record.IVMS_CHECK_DATE)}</td>
                        <td style={{ ...minutesStyle, ...rowStyle }}>
                          {minutesSinceLastCheck}
                        </td>
                        <td>{record.NEXT_POINT}</td>
                        {/* <td>{formatDateTime(record.NEXT_ARRIVAL_DATE)}</td> */}
                        <td>{record.OFFLOAD_POINT}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="aggregate-tables">
              <div className="table-scroll1">
                <table className="aggregate1" id="sub1">
                  <thead>
                    <tr>
                      <th>Journey Plane Date</th>
                      <th>Live JPS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(journeyPlaneDateCounts).map(
                      ([date, count], index) => (
                        <tr key={index}>
                          <td>{date}</td>
                          <td>{count}</td>
                        </tr>
                      )
                    )}
                    <tr>
                      <td>Total</td>
                      <td>
                        {Object.values(journeyPlaneDateCounts).reduce(
                          (a, b) => a + b,
                          0
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="table-scroll1">
                <table className="aggregate2" id="sub2">
                  <thead>
                    <tr>
                      <th>Tracker</th>
                      <th>Live JPS</th>
                      <th>Critical Check</th>
                      <th>Due For Checking</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(trackersCount).map(([tracker, counts]) => (
                      <tr key={tracker}>
                        <td>{tracker.toUpperCase()}</td>
                        <td>{counts.liveJPS}</td>
                        <td>{counts.criticalCheck}</td>
                        <td>{counts.dueForChecking}</td>
                      </tr>
                    ))}
                    <tr>
                      <td>Total</td>
                      <td>
                        {Object.values(trackersCount).reduce(
                          (sum, item) => sum + item.liveJPS,
                          0
                        )}
                      </td>
                      <td>
                        {Object.values(trackersCount).reduce(
                          (sum, item) => sum + item.criticalCheck,
                          0
                        )}
                      </td>
                      <td>
                        {cardCounts.dueForChecking}{" "}
                        {/* Use card count directly instead of reducer */}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
      {renderNotification()}
      <footer className="dashboard-header">
        <img src="pdo.png" alt="Left Logo" className="logoleft" />
        <div style={{ marginLeft: "auto" }}>
          <img src="almadinalogo.jpeg" alt="Right Logo" className="logoright" />
        </div>
      </footer>
    </div>
  );
};

export default Dashboard;
